/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import type {
  MembershipProgramWithStats,
  PromotionReward,
} from "@lemmonhq/types";
import { useTrpc } from "@app/vue/providers/index";
import {
  defaultRewardForType,
  PromotionRewardEditor,
} from "../../components/PromotionRewardEditor";
import { WalletDesignSelect } from "../../components/WalletDesignSelect";

export type MembershipProgramEditorProps = {
  program?: MembershipProgramWithStats;
  scope?: "shop" | "group";
  onClose: () => void;
  onSaved: (program: MembershipProgramWithStats) => void;
};

export const MembershipProgramEditor =
  defineComponent<MembershipProgramEditorProps>({
    name: "MembershipProgramEditor",
    props: ["program", "scope", "onClose", "onSaved"],
    setup(props) {
      const { t } = useLocalization();
      const trpc = useTrpc();
      const queryClient = useQueryClient();
      const isGroup = props.scope === "group";
      const form = ref({
        name: props.program?.name ?? "",
        // Null keeps the plain default design on create.
        wallet_pass_design_id: (props.program?.wallet_pass_design_id ??
          null) as string | null,
        description: props.program?.description ?? "",
        validity_months: props.program?.validity_months ?? null,
        cap_period: (props.program?.cap_period ?? null) as
          | "week"
          | "month"
          | "year"
          | null,
        cap_count: (props.program?.cap_count ?? null) as number | null,
        cap_amount: (props.program?.cap_amount ?? null) as number | null,
        period_timezone: (props.program?.period_timezone ?? null) as
          | string
          | null,
        enabled: props.program?.enabled ?? true,
        enrolment: (props.program?.enrolment ?? "manual") as "manual" | "auto",
        restaurantIds: props.program?.applicable_restaurant_ids ?? [],
        reward:
          props.program?.promotion?.reward ??
          defaultRewardForType("order_discount"),
      });

      // Group applicability: the restaurants a group program can enrol from.
      const shopsQuery = useQuery({
        queryKey: ["membershipEditorShops"],
        queryFn: () => trpc.shops.list.query(),
        enabled: isGroup,
      });
      const shops = computed(() => shopsQuery.data.value ?? []);
      const toggleRestaurant = (id: number) => {
        // An empty list renders as "all selected"; materialise it before
        // removing one so unchecking means all-except-this, not only-this.
        const ids =
          form.value.restaurantIds.length === 0
            ? shops.value.map((s) => s.id)
            : form.value.restaurantIds;
        form.value.restaurantIds = ids.includes(id)
          ? ids.filter((r) => r !== id)
          : [...ids, id];
      };

      const saveMutation = useMutation({
        mutationFn: async () => {
          const input = {
            name: form.value.name.trim(),
            description: form.value.description.trim() || null,
            validity_months: form.value.validity_months,
            cap_period: form.value.cap_period,
            cap_count: form.value.cap_count,
            cap_amount: form.value.cap_amount,
            period_timezone: form.value.period_timezone,
            enabled: form.value.enabled,
            enrolment: form.value.enrolment,
            // Only a non-empty subset is sent; empty means "leave the default
            // (all group shops)" rather than "applies nowhere".
            restaurant_ids:
              isGroup && form.value.restaurantIds.length > 0
                ? form.value.restaurantIds
                : null,
            reward: form.value.reward as any,
          };
          if (props.program) {
            const updateInput = {
              id: props.program.id,
              ...input,
              // A program always has a design; it can only switch to another.
              ...(form.value.wallet_pass_design_id
                ? { wallet_pass_design_id: form.value.wallet_pass_design_id }
                : {}),
            };
            return props.scope === "group"
              ? trpc.membershipPrograms.groupUpdate.mutate(updateInput)
              : trpc.membershipPrograms.update.mutate(updateInput);
          }
          const createInput = {
            ...input,
            wallet_pass_design_id: form.value.wallet_pass_design_id,
          };
          return props.scope === "group"
            ? trpc.membershipPrograms.groupCreate.mutate(createInput)
            : trpc.membershipPrograms.create.mutate(createInput);
        },
        onSuccess: (program) => {
          queryClient.invalidateQueries({ queryKey: ["membershipPrograms"] });
          props.onSaved(program as MembershipProgramWithStats);
          props.onClose();
        },
      });

      const valid = computed(() => form.value.name.trim().length > 0);

      const nullableNumber = (value: string) => {
        const number = Number.parseInt(value);
        return value && Number.isFinite(number) ? number : null;
      };

      return () => (
        <RightSheet
          isOpen
          onClose={props.onClose}
          title={
            <div class="flex-1 text-2xl text-accent">
              {props.program
                ? t("group.loyalty.membership_editor_edit")
                : t("group.loyalty.membership_editor_new")}
            </div>
          }
        >
          <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div class="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
              {saveMutation.error.value && (
                <div class="alert alert-error text-sm">
                  {(saveMutation.error.value as Error).message}
                </div>
              )}

              <section class="space-y-4">
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-medium">
                      {t("group.loyalty.program_name")}
                    </span>
                  </label>
                  <input
                    class="input input-bordered focus:input-primary"
                    value={form.value.name}
                    onInput={(event) =>
                      (form.value.name = (
                        event.target as HTMLInputElement
                      ).value)
                    }
                  />
                </div>
                <WalletDesignSelect
                  kind="membership"
                  scope={props.scope}
                  value={form.value.wallet_pass_design_id}
                  creating={!props.program}
                  onChange={(id: string | null) =>
                    (form.value.wallet_pass_design_id = id)
                  }
                />
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-medium">
                      {t("group.loyalty.program_description")}
                    </span>
                  </label>
                  <textarea
                    class="textarea textarea-bordered focus:textarea-primary"
                    value={form.value.description}
                    onInput={(event) =>
                      (form.value.description = (
                        event.target as HTMLTextAreaElement
                      ).value)
                    }
                  />
                </div>
              </section>

              <div class="divider my-0" />

              <section class="max-w-sm">
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-medium">
                      {t("group.loyalty.validity_months")}
                    </span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    class="input input-bordered"
                    value={form.value.validity_months ?? ""}
                    placeholder={t("group.loyalty.no_expiry")}
                    onInput={(event) =>
                      (form.value.validity_months = nullableNumber(
                        (event.target as HTMLInputElement).value,
                      ))
                    }
                  />
                </div>
              </section>

              <div class="divider my-0" />

              <section class="space-y-3">
                <label class="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-sm"
                    checked={
                      form.value.cap_count != null ||
                      form.value.cap_amount != null
                    }
                    onChange={(event) => {
                      const on = (event.target as HTMLInputElement).checked;
                      form.value.cap_period = on
                        ? (form.value.cap_period ?? "week")
                        : null;
                      if (on) {
                        // Default to a one-use cap; the merchant can switch to a
                        // budget cap or set both.
                        if (
                          form.value.cap_count == null &&
                          form.value.cap_amount == null
                        ) {
                          form.value.cap_count = 1;
                        }
                      } else {
                        form.value.cap_count = null;
                        form.value.cap_amount = null;
                        form.value.period_timezone = null;
                      }
                    }}
                  />
                  <span class="text-sm font-medium text-accent">
                    {t("group.loyalty.cap_enable")}
                  </span>
                </label>

                {(form.value.cap_count != null ||
                  form.value.cap_amount != null) && (
                  <div class="space-y-2">
                    <div class="flex items-end gap-2">
                      <label class="form-control">
                        <div class="label py-0 pb-1">
                          <span class="label-text">
                            {t("group.loyalty.cap_count_label")}
                          </span>
                        </div>
                        <input
                          type="number"
                          min={0}
                          placeholder="—"
                          class="input input-bordered input-sm w-24"
                          value={form.value.cap_count ?? ""}
                          onInput={(event) => {
                            const v = Number(
                              (event.target as HTMLInputElement).value,
                            );
                            form.value.cap_count =
                              Number.isFinite(v) && v > 0
                                ? Math.floor(v)
                                : null;
                          }}
                        />
                      </label>
                      <label class="form-control">
                        <div class="label py-0 pb-1">
                          <span class="label-text">
                            {t("group.loyalty.cap_amount_label")}
                          </span>
                        </div>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          placeholder="—"
                          class="input input-bordered input-sm w-28"
                          value={form.value.cap_amount ?? ""}
                          onInput={(event) => {
                            const v = Number(
                              (event.target as HTMLInputElement).value,
                            );
                            form.value.cap_amount =
                              Number.isFinite(v) && v > 0 ? v : null;
                          }}
                        />
                      </label>
                      <span class="pb-2 text-sm text-base-content/70">
                        {t("group.loyalty.cap_per")}
                      </span>
                      <select
                        class="select select-bordered select-sm"
                        value={form.value.cap_period ?? "week"}
                        onChange={(event) =>
                          (form.value.cap_period = (
                            event.target as HTMLSelectElement
                          ).value as "week" | "month" | "year")
                        }
                      >
                        <option value="week">
                          {t("group.loyalty.cap_week")}
                        </option>
                        <option value="month">
                          {t("group.loyalty.cap_month")}
                        </option>
                        <option value="year">
                          {t("group.loyalty.cap_year")}
                        </option>
                      </select>
                    </div>

                    {isGroup && (
                      <label class="form-control">
                        <div class="label py-0 pb-1">
                          <span class="label-text">
                            {t("group.loyalty.cap_timezone")}
                          </span>
                        </div>
                        <input
                          type="text"
                          class="input input-bordered input-sm w-64"
                          placeholder="Europe/Zurich"
                          value={form.value.period_timezone ?? ""}
                          onInput={(event) =>
                            (form.value.period_timezone =
                              (event.target as HTMLInputElement).value.trim() ||
                              null)
                          }
                        />
                      </label>
                    )}

                    <p class="text-xs text-base-content/60">
                      {t("group.loyalty.cap_hint")}
                    </p>
                  </div>
                )}
              </section>

              <div class="divider my-0" />

              <section class="space-y-3">
                <div class="text-sm font-medium text-accent">
                  {t("group.loyalty.enrolment")}
                </div>
                <div class="flex flex-col gap-2 sm:flex-row">
                  {(["manual", "auto"] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      class={`flex-1 rounded-lg border p-3 text-left transition-colors ${
                        form.value.enrolment === mode
                          ? "border-primary bg-primary/5"
                          : "border-base-300 hover:border-base-content/30"
                      }`}
                      onClick={() => (form.value.enrolment = mode)}
                    >
                      <div class="flex items-center gap-2">
                        <span
                          class={`material-symbols-outlined text-lg ${
                            form.value.enrolment === mode
                              ? "text-primary"
                              : "text-base-content/40"
                          }`}
                        >
                          {mode === "manual" ? "list" : "groups"}
                        </span>
                        <span class="text-sm font-medium text-accent">
                          {mode === "manual"
                            ? t("group.loyalty.enrolment_manual")
                            : t("group.loyalty.enrolment_auto")}
                        </span>
                      </div>
                      <div class="mt-1 text-xs text-base-content/50">
                        {mode === "manual"
                          ? t("group.loyalty.enrolment_manual_hint")
                          : t("group.loyalty.enrolment_auto_hint")}
                      </div>
                    </button>
                  ))}
                </div>

                {isGroup && (
                  <div class="space-y-2">
                    <div class="text-xs font-medium text-base-content/60">
                      {t("group.loyalty.applies_at")}
                    </div>
                    <div class="text-[11px] text-base-content/40">
                      {t("group.loyalty.applies_at_hint")}
                    </div>
                    <div class="flex flex-col gap-1">
                      {shops.value.map((shop) => {
                        const selected =
                          form.value.restaurantIds.length === 0 ||
                          form.value.restaurantIds.includes(shop.id);
                        return (
                          <label
                            key={shop.id}
                            class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-base-200"
                          >
                            <input
                              type="checkbox"
                              class="checkbox checkbox-primary checkbox-sm"
                              checked={selected}
                              onChange={() => toggleRestaurant(shop.id)}
                            />
                            <span class="text-sm text-accent">{shop.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>

              <div class="divider my-0" />

              <PromotionRewardEditor
                value={form.value.reward}
                onChange={(reward: PromotionReward) =>
                  (form.value.reward = reward)
                }
                title={t("group.loyalty.member_reward")}
                allowProductReward={props.scope !== "group"}
                allowedRewardTypes={
                  props.scope === "group" ? ["order_discount"] : undefined
                }
              />

              <label class="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  class="toggle toggle-primary toggle-sm"
                  checked={form.value.enabled}
                  onChange={(event) =>
                    (form.value.enabled = (
                      event.target as HTMLInputElement
                    ).checked)
                  }
                />
                <span class="label-text">{t("group.loyalty.enabled")}</span>
              </label>
            </div>
            <div class="sticky bottom-0 shrink-0 border-t bg-base-100 p-4">
              <button
                type="button"
                class="btn btn-primary btn-block"
                disabled={!valid.value || saveMutation.isPending.value}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isPending.value && (
                  <span class="loading loading-spinner loading-xs" />
                )}
                {t("group.loyalty.save")}
              </button>
            </div>
          </div>
        </RightSheet>
      );
    },
  });
